import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  forwardRef,
  HostBinding,
  inject,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FmntsRatingModule } from '@fmnts/components/rating';
import { Option } from '@fmnts/core';
import {
  AbstractDeclarativeInputFieldComponent,
  FormSelectField,
} from '@fmnts/declarative-forms';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';

type FieldValue = Option<number>;

/**
 * Form field component that shows an input for entering a
 * number rating.
 */
@Component({
  selector: 'next-rating-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './rating-field.component.html',
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule, FmntsRatingModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatingFieldComponent),
      multi: true,
    },
  ],
})
export class RatingFieldComponent
  extends AbstractDeclarativeInputFieldComponent<FormSelectField, FieldValue>
  implements DoCheck
{
  private readonly cd = inject(ChangeDetectorRef);

  @HostBinding('class') componentClass = 'form-field form-field__rating';

  /** Used to perform change detection checks */
  private _wasTouched = false;

  /** Icon to use for displaying the rating */
  public icon: IconProp = faStar;

  /**
   * Current value of the field
   */
  public get value() {
    return this._value;
  }
  public set value(v: FieldValue) {
    this._value = v;
    this._onChange(this._value);
    this._onTouched();
  }
  private _value: FieldValue = null;

  ngDoCheck(): void {
    // Form and field are needed to perform checks
    if (!this.form || !this.field) {
      return;
    }

    // Control is needed to perform checks
    const { control } = this;
    if (!control) {
      return;
    }

    // Perform change detection checks
    // Check if `touched` changed
    if (control.touched !== this._wasTouched) {
      this._wasTouched = control.touched;
      this.cd.markForCheck();
    }
  }

  writeValue(val: FieldValue): void {
    this._value = val;
  }

  get showError(): boolean {
    return (
      this.form && (this.value || this.control.touched) && this.control.invalid
    );
  }
}
